/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import type { MutableRefObject } from 'react';

import { setLocation } from 'js/lib/coursera.redirect';

import { Button, Link, PageGridContainer } from '@coursera/cds-core';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import useBuildOnboardingUrl from 'bundles/onboarding-common/hooks/useBuildOnboardingUrl';
import HomeSection from 'bundles/page-config-common/components/shared/HomeSection';
import {
  useIsLearnerProfileLoading,
  useLearnerGoal,
  useLearnerRoles,
} from 'bundles/page-config-common/contexts/consumer/LearnerProfileContext';
import { getCoachOnboardingCopy } from 'bundles/page-config-common/utils/getCoachOnboardingCopy';
import { getEditCareerGoalCopy } from 'bundles/page-config-common/utils/getEditCareerGoalCopy';
import { careerGoalAndRoleTrackingLinkName } from 'bundles/page-config-common/utils/getEventingData';
import inServerContext from 'bundles/ssr/util/inServerContext';
import CareerGoalBannerModule from 'bundles/unified-home-common/components/consumer/CareerGoalBannerModule';
import CoachOnboardingBannerModule from 'bundles/unified-home-common/components/consumer/CoachOnboardingBannerModule';
import { useInferredRole } from 'bundles/unified-home-common/contexts/InferredRoleContext';
import { usePathwayCompletedStateContext } from 'bundles/unified-home-common/contexts/PathwayCompletedStateContext';
import showInferredRole from 'bundles/unified-home-common/utils/showInferredRole';

import _t from 'i18n!nls/consumer-home';

const styles = {
  careerGoalButton: css`
    font-weight: 600;
  `,
};

type Props = {
  // NOTE: move to PCS component (https://coursera.atlassian.net/browse/RP-1081)
  hideWhenOnboardingCompleted?: boolean;
  twoColumnLayout?: boolean;
};

const CoachOnboardingBannerModuleRenderer = (props: Props) => {
  const { hideWhenOnboardingCompleted, twoColumnLayout } = props;
  const { showCompletedState } = usePathwayCompletedStateContext();
  const goal = useLearnerGoal();
  const roles = useLearnerRoles();
  const isLearnerProfileLoading = useIsLearnerProfileLoading();
  const { inferredRole, setCareerRole, isSettingCareerRole, inferredRoleLoading } = useInferredRole();
  const { onboardingUrl } = useBuildOnboardingUrl({});
  const trackV3 = useTracker();

  const hasCompletedOnboarding = goal && roles && roles.length > 0;
  const linkName = careerGoalAndRoleTrackingLinkName(!!goal, !!roles?.length);

  const editOnboardingTrackingData = {
    button: { name: 'onboarding_edit' as const, linkName, linkURL: onboardingUrl },
  };
  const editOnboardingVisibilityRef: MutableRefObject<HTMLAnchorElement | HTMLButtonElement | null> =
    useVisibilityTracker('view_button', editOnboardingTrackingData);

  const confirmInferredRoleTrackingData = {
    button: { name: 'confirm_inferred_role' as const },
    role: {
      roleSlug: inferredRole?.slug ?? '',
    },
  };
  const confirmInferredRoleVisibilityRef: MutableRefObject<HTMLButtonElement | null> = useVisibilityTracker(
    'view_button',
    confirmInferredRoleTrackingData
  );

  if (isLearnerProfileLoading) {
    return null;
  }

  if (hasCompletedOnboarding) {
    if (hideWhenOnboardingCompleted) {
      return null;
    } else {
      const careerGoalMessage = getEditCareerGoalCopy(goal, roles, (roleSlug) => {
        trackV3('click_button', {
          button: {
            linkName: 'my_learning_rdp_link',
            name: 'role_info',
            linkURL: `/career-academy/roles/${roleSlug}`,
          },
          role: { roleSlug },
        });
      });
      const careerGoalBanner = (
        <CareerGoalBannerModule
          careerGoal={careerGoalMessage}
          ctaButton={
            <Link
              ref={editOnboardingVisibilityRef as MutableRefObject<HTMLAnchorElement | null>}
              variant="quiet"
              typographyVariant="bodyPrimary"
              href={onboardingUrl}
              css={styles.careerGoalButton}
              onClick={() => trackV3('click_button', editOnboardingTrackingData)}
            >
              {_t('Edit goal')}
            </Link>
          }
        />
      );
      return twoColumnLayout ? careerGoalBanner : <PageGridContainer>{careerGoalBanner}</PageGridContainer>;
    }
  } else {
    const showInferredRoleState = (!roles || roles.length === 0) && showInferredRole() && !!inferredRole;

    if (inServerContext || inferredRoleLoading) {
      const coachOnboardingBanner = (
        <CoachOnboardingBannerModule
          ctaButton={
            <Button variant="ghost" size="medium" edgeAlign="start">
              {' '}
            </Button>
          }
        />
      );
      return twoColumnLayout ? coachOnboardingBanner : <PageGridContainer>{coachOnboardingBanner}</PageGridContainer>;
    }

    const { text, cta } = getCoachOnboardingCopy(
      goal,
      roles,
      showCompletedState,
      showInferredRoleState ? inferredRole : undefined
    );

    const coachOnboardingBanner = (
      <CoachOnboardingBannerModule
        text={text}
        ctaButton={
          showInferredRoleState ? (
            <Button
              ref={confirmInferredRoleVisibilityRef}
              variant="ghost"
              size="small"
              edgeAlign="start"
              onClick={() => {
                setCareerRole();
                trackV3('click_button', confirmInferredRoleTrackingData);
              }}
              loading={isSettingCareerRole}
            >
              {cta}
            </Button>
          ) : (
            <Button
              ref={editOnboardingVisibilityRef as MutableRefObject<HTMLButtonElement | null>}
              variant="ghost"
              size="small"
              edgeAlign="start"
              onClick={() => {
                trackV3('click_button', editOnboardingTrackingData);
                setLocation(onboardingUrl);
              }}
            >
              {cta}
            </Button>
          )
        }
        isDismissable={showInferredRoleState}
      />
    );
    return twoColumnLayout ? (
      <HomeSection className="coach-onboarding-banner-module">{coachOnboardingBanner}</HomeSection>
    ) : (
      <PageGridContainer>{coachOnboardingBanner}</PageGridContainer>
    );
  }
};

export default CoachOnboardingBannerModuleRenderer;
